/**
 * One place for every number that reaches the screen, so the same figure is
 * never formatted two ways in two components.
 *
 * Money arrives from the API as a string, because CLAUDE.md rule 6 keeps
 * NUMERIC out of JS floats. These helpers accept the string and format it
 * without ever doing arithmetic on it.
 */

const CURRENCY = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  maximumFractionDigits: 0,
});

const CURRENCY_COMPACT = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  notation: 'compact',
  maximumFractionDigits: 1,
});

const INTEGER = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

function toNumber(value: string | number | null | undefined): number | null {
  if (value === null || value === undefined) return null;
  const parsed = typeof value === 'number' ? value : Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

/** `$6,000`. Returns the em dash for null, so tiles never render "$NaN". */
export function money(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  return parsed === null ? '—' : CURRENCY.format(parsed);
}

/** `$1.2M`, for axis labels and tight cards. */
export function moneyCompact(value: string | number | null | undefined): string {
  const parsed = toNumber(value);
  return parsed === null ? '—' : CURRENCY_COMPACT.format(parsed);
}

export function count(value: number | null | undefined): string {
  return value === null || value === undefined ? '—' : INTEGER.format(value);
}

/** `0.42` renders as `42%`. */
export function percent(fraction: number | null | undefined, digits = 0): string {
  if (fraction === null || fraction === undefined || !Number.isFinite(fraction)) return '—';
  return `${(fraction * 100).toFixed(digits)}%`;
}

/** Signed, for deltas: `+12.4%`, `-3%`, `0%`. */
export function signedPercent(fraction: number, digits = 1): string {
  const pct = fraction * 100;
  const sign = pct > 0 ? '+' : '';
  // toFixed(1) on -0.04 gives "-0.0"; normalise so it reads as 0.
  const text = pct.toFixed(digits);
  return `${sign}${text === '-0.0' || text === '-0' ? '0' : text}%`;
}

export function signedCount(value: number): string {
  return `${value > 0 ? '+' : ''}${INTEGER.format(value)}`;
}

/** `3d`, `12d`, `‹1d`. Stage age on a pipeline card has no room for words. */
export function days(value: number | null | undefined): string {
  if (value === null || value === undefined) return '—';
  if (value < 1) return '<1d';
  return `${Math.round(value)}d`;
}

const DATE_ONLY = /^(\d{4})-(\d{2})-(\d{2})$/;

/**
 * A DATE column (payments.due_date) has no time and no zone. Passing it to
 * `new Date()` parses it as UTC midnight, which then renders as the previous
 * day anywhere west of Greenwich — a payment due the 1st displays as the
 * 31st. Date-only strings are therefore built in local time.
 *
 * TIMESTAMPTZ values carry a time and an offset and are parsed normally,
 * which is what CLAUDE.md rule 7 wants: stored UTC, rendered locally.
 */
function toDate(value: string | Date): Date {
  if (value instanceof Date) return value;

  const parts = DATE_ONLY.exec(value);
  if (parts) {
    return new Date(Number(parts[1]), Number(parts[2]) - 1, Number(parts[3]));
  }
  return new Date(value);
}

/** `Mar 4`, or `Mar 4, 2025` when the year is not the current one. */
export function shortDate(value: string | Date | null | undefined): string {
  if (!value) return '—';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '—';
  const sameYear = date.getFullYear() === new Date().getFullYear();
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
}

export function timeOfDay(value: string | Date | null | undefined): string {
  if (!value) return '';
  const date = toDate(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
}

/** `48m`, `1h 12m`. Call durations are stored in seconds. */
export function duration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || seconds < 0) return '—';
  const totalMinutes = Math.round(seconds / 60);
  const hours = Math.floor(totalMinutes / 60);
  const minutes = totalMinutes % 60;
  return hours > 0 ? `${hours}h ${minutes}m` : `${minutes}m`;
}
